// Builds the nightly EOD report as a PDF (A4): a summary page, then the venue's own sections (from its report setup).
// jsPDF is loaded only when a report is generated, so it doesn't weigh down the app.
import { computeEod, DENOMS, sectionOn, DEFAULT_SURCHARGE_TYPES, DEFAULT_STATIONS_FRONT, DEFAULT_STATIONS_KITCHEN, DEFAULT_NOTE_TOPICS, type EodConfig, type EodData, type EodSectionId } from '@/src/lib/eod';

export interface TeamNote { name: string; answers: Record<string, string> }
/** One earlier trading day, for comparisons and the 7-day chart. */
export interface HistoryDay { date: string; total: number | null; covers: number | null; labourPct: number | null; closed?: boolean }
export interface PdfSummary { stood_out: string[]; watch: string[]; team?: Array<{ topic: string; summary: string }> }

const cents = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const m = (n: number | null | undefined) => (n == null ? '—' : cents.format(n));
const pct = (n: number | null | undefined) => (n == null ? '—' : `${n}%`);

// The built-in PDF fonts only cover Western European characters: map anything else to its
// closest plain letter (ı→i, ş→s, ğ→g, −→-) so names and notes never come out garbled.
const WINANSI_EXTRA = new Set('–—‘’“”•…€‚„†‡ˆ‰Š‹ŒŽ˜™š›œžŸ');
const SPECIAL: Record<string, string> = { 'ı': 'i', 'İ': 'I', '−': '-', '✓': 'OK', '✔': 'OK', '→': '->', '≈': '~' };
export function pdfSafe(t: string): string {
  let out = '';
  for (const ch of t) {
    const code = ch.codePointAt(0) ?? 0;
    if (code <= 0xff || WINANSI_EXTRA.has(ch)) { out += ch; continue; }
    if (SPECIAL[ch]) { out += SPECIAL[ch]; continue; }
    const base = ch.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    out += base && (base.codePointAt(0) ?? 0) <= 0xff ? base : '?';
  }
  return out;
}

export async function buildEodPdf(p: {
  venueName: string; date: string; data: EodData; config: EodConfig; submittedBy: string; submittedAt: Date; team: TeamNote[];
  history?: HistoryDay[]; summary?: PdfSummary | null; weather?: string | null;
}): Promise<Blob> {
  const [{ jsPDF }, atMod] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  // The plugin's export shape differs between bundlers: take whichever is the function.
  // deno-lint-ignore no-explicit-any
  const autoTable = ((atMod as any).autoTable ?? (atMod as any).default?.default ?? atMod.default) as typeof atMod.default;
  const d = p.data;
  const cfg = p.config;
  const k = computeEod(d, cfg);
  const on = (id: EodSectionId) => sectionOn(cfg, id);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const M = 14;
  const CW = W - 2 * M;
  type RGB = [number, number, number];
  const GREEN: RGB = [47, 107, 47], GREEN_L: RGB = [201, 223, 201], GOLD: RGB = [232, 160, 32], RED: RGB = [192, 57, 43], OK: RGB = [47, 125, 50];
  const INK: RGB = [26, 26, 23], DIM: RGB = [107, 107, 99], FAINT: RGB = [166, 166, 154], LINE: RGB = [228, 228, 220], TINT: RGB = [245, 245, 240];
  const TOP = 26;
  let y = TOP;
  const color = (c: RGB) => doc.setTextColor(...c);
  const font = (size: number, bold = false) => { doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(size); };
  const txt = (t: string, x: number, yy: number, o?: { align?: 'left' | 'right' | 'center'; maxWidth?: number }) => doc.text(pdfSafe(t), x, yy, o);
  const ensure = (need: number) => { if (y + need > 282) { doc.addPage(); y = TOP; } };

  const dayLong = new Date(`${p.date}T12:00:00`).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const dayShort = new Date(`${p.date}T12:00:00`).toLocaleDateString('en-AU', { weekday: 'short' });

  // ---------------- page 1: summary ----------------
  font(8.5); color(DIM);
  txt([dayLong, `Manager: ${d.manager || p.submittedBy}`, `Submitted ${p.submittedAt.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })} by ${p.submittedBy}`, p.weather ?? ''].filter(Boolean).join('  ·  '), M, y);
  y += 5;

  const hist = (p.history ?? []).filter((h) => h.date < p.date).sort((a, b) => a.date.localeCompare(b.date));
  const lastWeek = hist.find((h) => h.date === shiftIso(p.date, -7)) ?? null;
  const vs = `vs last ${dayShort}`;
  const change = (cur: number | null, prev: number | null | undefined, invert = false, points = false): { t: string; c: RGB } | null => {
    if (cur == null || prev == null || (!points && prev === 0)) return null;
    const dlt = points ? cur - prev : ((cur - prev) / prev) * 100;
    if (Math.abs(dlt) < 0.05) return { t: `= ${vs}`, c: DIM };
    const good = invert ? dlt < 0 : dlt > 0;
    return { t: `${dlt > 0 ? '+' : '-'}${Math.abs(dlt).toFixed(1)}${points ? ' pts' : '%'} ${vs}`, c: good ? OK : RED };
  };
  const zero = (x: number | null) => x != null && Math.abs(x) < 0.01;
  const tgt = cfg.targets ?? {};
  type Tile = { label: string; value: string; sub?: string; delta?: { t: string; c: RGB } | null };
  const tiles: Tile[] = [
    { label: 'Total sales (inc GST)', value: m(k.total), sub: k.food != null ? `Food ${m(k.food)} · Bev ${m(k.bev)}` : undefined, delta: change(k.total, lastWeek?.total) },
    { label: 'Covers', value: k.covers != null ? String(k.covers) : '—', sub: on('services') && cfg.services.length > 1 ? cfg.services.map((s) => `${s} ${k.services[s]?.covers ?? '—'}`).join(' · ') : undefined, delta: change(k.covers, lastWeek?.covers) },
    { label: '$ per head', value: m(k.perHead), sub: 'inc GST', delta: change(k.perHead, lastWeek?.total != null && lastWeek.covers ? lastWeek.total / lastWeek.covers : null) },
  ];
  if (on('labour')) tiles.push({ label: 'Labour % (ex GST)', value: k.labourPct != null ? `${k.labourPct}%` : '—', sub: `${m(k.labourTotal)}${tgt.labourPct != null ? ` · target ${tgt.labourPct}%` : ''}`, delta: change(k.labourPct, lastWeek?.labourPct, true, true) });
  if (on('cash') || on('payments')) {
    const both = [on('cash') ? k.cashVariance : null, on('payments') ? k.paymentsVariance : null].filter((x) => x !== undefined);
    const allOk = both.every((x) => x == null || zero(x));
    tiles.push({ label: on('cash') && on('payments') ? 'Cash / payments' : on('cash') ? 'Cash variance' : 'Payments variance',
      value: [on('cash') ? m(k.cashVariance) : null, on('payments') ? m(k.paymentsVariance) : null].filter(Boolean).join(' / '),
      sub: on('cash') && on('payments') ? 'Variance vs POS / vs sales' : 'Variance', delta: { t: allOk ? 'Balanced' : 'Check the explanation', c: allOk ? OK : RED } });
  }
  if (on('discounts')) {
    const dd = (k.discountsTotal ?? 0) + (k.deletedAmount ?? 0);
    tiles.push({ label: 'Discounts & deleted', value: m(dd), sub: k.total ? `${((dd / k.total) * 100).toFixed(1)}% of sales · ${d.discounts.filter((x) => x.amount).length} discounts` : undefined });
  }
  if (tiles.length % 3) { /* keep rows even */ }
  const tw = (CW - 6) / 3, th = 22;
  tiles.forEach((t, i) => {
    const col = i % 3, row = Math.floor(i / 3);
    const x = M + col * (tw + 3), yy = y + row * (th + 3);
    doc.setFillColor(...TINT); doc.setDrawColor(...LINE); doc.setLineWidth(0.2); doc.roundedRect(x, yy, tw, th, 1.5, 1.5, 'FD');
    font(6.3, true); color(DIM); txt(t.label.toUpperCase(), x + 3, yy + 4.6);
    font(14, true); color(INK); txt(t.value, x + 3, yy + 11.5, { maxWidth: tw - 6 });
    let ly = yy + 15.6;
    if (t.delta) { font(7, true); color(t.delta.c); txt(t.delta.t, x + 3, ly); ly += 3.6; }
    if (t.sub) { font(6.8); color(DIM); txt(t.sub, x + 3, ly, { maxWidth: tw - 6 }); }
  });
  y += Math.ceil(tiles.length / 3) * (th + 3) + 1;

  // AI summary
  if (p.summary && (p.summary.stood_out.length || p.summary.watch.length)) {
    const linesOut: Array<{ t: string; head?: boolean }> = [];
    if (p.summary.stood_out.length) { linesOut.push({ t: 'WHAT STOOD OUT', head: true }); p.summary.stood_out.forEach((b) => linesOut.push({ t: `•  ${b}` })); }
    if (p.summary.watch.length) { linesOut.push({ t: 'WATCH TOMORROW', head: true }); p.summary.watch.forEach((b) => linesOut.push({ t: `•  ${b}` })); }
    font(8.6);
    const wrapped = linesOut.flatMap((l) => (l.head ? [l] : (doc.splitTextToSize(pdfSafe(l.t), CW - 10) as string[]).map((t) => ({ t }))));
    const h = wrapped.length * 4.1 + 5;
    doc.setFillColor(238, 246, 238); doc.rect(M, y, CW, h, 'F'); doc.setFillColor(...GREEN); doc.rect(M, y, 1.1, h, 'F');
    let ly = y + 5;
    wrapped.forEach((l) => { if ((l as { head?: boolean }).head) { font(7.3, true); color(GREEN); } else { font(8.6); color(INK); } doc.text(l.t, M + 5, ly); ly += 4.1; });
    y += h + 5;
  }

  // Last 7 days chart: bars for each trading day, tonight highlighted, dashed line = same day the week before.
  const days = Array.from({ length: 7 }, (_, i) => shiftIso(p.date, i - 6));
  const byDate = new Map(hist.map((h) => [h.date, h]));
  const vals = days.map((dt) => (dt === p.date ? { total: k.total, closed: false } : { total: byDate.get(dt)?.total ?? null, closed: !!byDate.get(dt)?.closed || byDate.get(dt) == null }));
  const prevVals = days.map((dt) => byDate.get(shiftIso(dt, -7))?.total ?? null);
  const maxV = Math.max(1, ...vals.map((v) => v.total ?? 0), ...prevVals.map((v) => v ?? 0));
  if (vals.filter((v) => v.total).length >= 2) {
    ensure(52);
    font(10.5, true); color(INK); txt('Sales, last 7 days', M, y + 3); y += 6;
    const ch = 34, base = y + ch, slot = CW / 7, bw = slot * 0.55;
    font(6.5); color(DIM); doc.setDrawColor(...GOLD); doc.setLineWidth(0.25); doc.setLineDashPattern([1, 0.8], 0); doc.line(M + CW - 50, y + 1, M + CW - 44, y + 1); doc.setLineDashPattern([], 0);
    txt('Same day the week before', M + CW - 42, y + 2);
    const pts: Array<[number, number] | null> = [];
    vals.forEach((v, i) => {
      const cx = M + slot * i + slot / 2;
      const tonight = days[i] === p.date;
      if (v.total) {
        const h = (v.total / maxV) * (ch - 8);
        doc.setFillColor(...(tonight ? GREEN : GREEN_L)); doc.rect(cx - bw / 2, base - h, bw, h, 'F');
        font(6.6, tonight); color(tonight ? INK : DIM); txt(`$${(v.total / 1000).toFixed(1)}k`, cx, base - h - 1.5, { align: 'center' });
      } else { font(6.4); color(FAINT); txt('—', cx, base - 2, { align: 'center' }); }
      const pv = prevVals[i];
      pts.push(pv ? [cx, base - (pv / maxV) * (ch - 8)] : null);
      font(7, tonight); color(tonight ? INK : DIM);
      txt(new Date(`${days[i]}T12:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric' }), cx, base + 4, { align: 'center' });
    });
    doc.setDrawColor(...GOLD); doc.setLineWidth(0.45); doc.setLineDashPattern([1.2, 0.9], 0);
    for (let i = 1; i < pts.length; i++) { const a = pts[i - 1], b = pts[i]; if (a && b) doc.line(a[0], a[1], b[0], b[1]); }
    doc.setLineDashPattern([], 0); doc.setFillColor(...GOLD);
    pts.forEach((pt) => { if (pt) doc.circle(pt[0], pt[1], 0.7, 'F'); });
    doc.setDrawColor(...LINE); doc.setLineWidth(0.2); doc.line(M, base, M + CW, base);
    y = base + 9;
  }

  // By service, on the summary page too (when the venue splits sales by service).
  const svcList = on('services') && cfg.services.length > 1 ? cfg.services : [];
  if (svcList.length && y < 235) {
    font(10.5, true); color(INK); doc.text('By service', M, y + 3); y += 5;
    autoTable(doc, {
      startY: y, margin: { left: M, right: M, top: TOP }, theme: 'striped', styles: { fontSize: 8.6, cellPadding: 1.5 },
      headStyles: { fillColor: [255, 255, 255], textColor: DIM, fontStyle: 'bold', lineColor: INK, lineWidth: { bottom: 0.3 } as never }, alternateRowStyles: { fillColor: TINT },
      head: [['Service', 'Sales', 'Food', 'Beverage', 'Food / bev', 'Covers', '$ / head'].map(pdfSafe)],
      body: [...svcList.map((sv) => { const x = k.services[sv]; const fp = x?.sales && x.food != null ? Math.round((x.food / x.sales) * 100) : null;
        return [sv, m(x?.sales), m(x?.food), m(x?.bev), fp != null ? `${fp}% / ${100 - fp}%` : '—', x?.covers != null ? String(x.covers) : '—', m(x?.perHead)]; }),
        ['Total', m(k.total), m(k.food), m(k.bev), k.foodPct != null ? `${Math.round(k.foodPct)}% / ${100 - Math.round(k.foodPct)}%` : '—', k.covers != null ? String(k.covers) : '—', m(k.perHead)]].map((r) => r.map(pdfSafe)),
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
      didParseCell: (h) => { if (h.section === 'head' && h.column.index > 0) h.cell.styles.halign = 'right'; if (h.section === 'body' && h.row.index === svcList.length) { h.cell.styles.fontStyle = 'bold'; h.cell.styles.fillColor = [255, 255, 255]; } },
    });
  }

  // ---------------- shared table helpers ----------------
  // deno-lint-ignore no-explicit-any
  const after = () => ((doc as any).lastAutoTable?.finalY ?? y) + 5;
  let letter = 0;
  const heading = (title: string, rows = 4, lettered = true) => {
    const need = 10 + Math.min(rows, 18) * 6;
    ensure(need);
    font(11, true); color(GREEN);
    txt(lettered ? `${String.fromCharCode(65 + letter++)}. ${title}` : title, M, y + 4); color(INK);
    y += 7;
  };
  const kv = (rows: Array<[string, string]>, bold: number[] = [], red: number[] = []) => {
    autoTable(doc, {
      startY: y, margin: { left: M, right: M, top: TOP }, theme: 'plain', styles: { fontSize: 9, cellPadding: 1.3, lineColor: LINE, lineWidth: { bottom: 0.15 } as never },
      columnStyles: { 0: { cellWidth: 100, textColor: [80, 80, 76] }, 1: { halign: 'right' } },
      body: rows.map((r) => r.map(pdfSafe)),
      didParseCell: (h) => { if (bold.includes(h.row.index)) h.cell.styles.fontStyle = 'bold'; if (red.includes(h.row.index) && h.column.index === 1) h.cell.styles.textColor = RED; },
    });
    y = after();
  };
  const table = (head: string[], body: string[][], rightCols: number[] = [], opts: { totalRow?: boolean; widths?: number[] } = {}) => {
    autoTable(doc, {
      startY: y, margin: { left: M, right: M, top: TOP }, head: [head.map(pdfSafe)], body: body.map((r) => r.map(pdfSafe)), theme: 'striped',
      styles: { fontSize: 8.8, cellPadding: 1.5 }, headStyles: { fillColor: [255, 255, 255], textColor: DIM, fontStyle: 'bold', lineColor: INK, lineWidth: { bottom: 0.3 } as never },
      alternateRowStyles: { fillColor: TINT },
      columnStyles: Object.fromEntries([...rightCols.map((c) => [c, { halign: 'right' as const }]), ...(opts.widths ?? []).map((w, i) => [i, { ...(rightCols.includes(i) ? { halign: 'right' as const } : {}), cellWidth: w }])]),
      didParseCell: (h) => {
        if (h.section === 'head' && rightCols.includes(h.column.index)) h.cell.styles.halign = 'right';
        if (opts.totalRow && h.section === 'body' && h.row.index === body.length - 1) { h.cell.styles.fontStyle = 'bold'; h.cell.styles.fillColor = [255, 255, 255]; }
      },
    });
    y = after();
  };
  const variance = (v: number | null) => (v == null ? '—' : zero(v) ? '$0.00  (balanced)' : m(v));
  const pctOf = (v: number | null | undefined) => (v && k.total ? `${((v / k.total) * 100).toFixed(1)}%` : '—');

  // ---------------- sections ----------------
  doc.addPage(); y = TOP;
  font(13, true); color(INK); txt('End of day report', M, y + 2); y += 5;
  font(8); color(DIM); txt('Figures from the POS close and X-reads, the cash count and the manager.', M, y + 1); y += 6;

  // Sales (always)
  const svcs = on('services') && cfg.services.length > 1 ? cfg.services : [];
  heading('Sales', svcs.length ? 8 : 7);
  if (svcs.length) {
    const col = (f: (s: string) => string, tot: string) => [...svcs.map(f), tot];
    table(['', ...svcs, 'Total'], [
      ['Total sales', ...col((s) => m(k.services[s]?.sales), m(k.total))],
      ['Food sales', ...col((s) => m(k.services[s]?.food), m(k.food))],
      ['Beverage sales', ...col((s) => m(k.services[s]?.bev), m(k.bev))],
      ['Food %', ...col((s) => { const v = k.services[s]; return v?.sales && v.food != null ? `${((v.food / v.sales) * 100).toFixed(1)}%` : '—'; }, k.foodPct != null ? `${k.foodPct}%` : '—')],
      ['Covers', ...col((s) => (k.services[s]?.covers != null ? String(k.services[s]?.covers) : '—'), k.covers != null ? String(k.covers) : '—')],
      ['$ per head', ...col((s) => m(k.services[s]?.perHead), m(k.perHead))],
    ], svcs.map((_, i) => i + 1).concat([svcs.length + 1]));
    kv([['Sales ex GST', m(k.exGst)]]);
  } else {
    kv([['Total sales (inc GST)', m(k.total)], ['Food', m(k.food)], ['Beverage', m(k.bev)], ['Food %', pct(k.foodPct)], ['Sales ex GST', m(k.exGst)], ['Covers', k.covers != null ? String(k.covers) : '—'], ['$ per head', m(k.perHead)]], [0]);
  }

  if (on('cash')) {
    heading('Cash reconciliation', d.cash.note.trim() ? 8 : 7);
    const counts = d.cash.noCash ? 'No cash taken (till = float)'
      : DENOMS.filter((dn) => d.cash.counts[dn.key]).map((dn) => `${d.cash.counts[dn.key]} × ${dn.label}`).join(', ') || '—';
    kv([
      ['Total cash per the POS', m(k.systemCash)],
      ['Opening float', `${m(d.cash.float)}${d.cash.openedBy ? `  (${d.cash.openedBy})` : ''}`],
      ['Closing count', counts],
      ['Closing total cash in till', `${m(k.counted)}${d.cash.countedBy ? `  (${d.cash.countedBy})` : ''}`],
      ['Cash takings (closing − float)', m(k.cashTakings)],
      ['Cash variance', variance(k.cashVariance)],
      ...(d.cash.note.trim() ? [['Explanation', d.cash.note.trim()] as [string, string]] : []),
    ], [5], zero(k.cashVariance) ? [] : [5]);
  }

  if (on('payments')) {
    heading('Payments', 12);
    kv([
      ['Cash', m(k.cashTakings ?? k.systemCash)], ['All cards incl. tips', m(k.cardsIncTips)], ['Less: card tips', k.cardTips != null ? `- ${m(k.cardTips)}` : '—'],
      ['Deposits & prepayments', m(d.payments.deposits)], ['Vouchers redeemed', m(k.vouchers)], ['Online / web', m(k.online)],
      ['Total payments (excl. tips)', m(k.paymentsTotal)], ['System total sales', m(k.total)], ['Payments variance', variance(k.paymentsVariance)],
      ...(d.payments.note.trim() ? [['Variance explanation', d.payments.note.trim()] as [string, string]] : []),
    ], [6, 8], zero(k.paymentsVariance) ? [] : [8]);
    const c = d.close ?? {};
    const cards = [['Visa', c.visa], ['Mastercard', c.mastercard], ['Amex', c.amex], ['Diners', c.diners], ['JCB', c.jcb], ['Alipay', c.alipay], ['EFTPOS savings', c.eftpos_savings], ['EFTPOS cheque', c.eftpos_cheque]]
      .filter(([, v]) => v) as Array<[string, number]>;
    if (cards.length) table(['Card', 'Amount'], cards.map(([n, v]) => [n, m(v)]), [1]);
  }

  if (on('surcharges')) {
    const types = cfg.surchargeTypes?.length ? cfg.surchargeTypes : DEFAULT_SURCHARGE_TYPES;
    heading('Surcharges (reference)', types.length + 2);
    table(['Card type', 'Surcharge'], [...types.map((t) => [t, m(d.surcharges?.[t] ?? null)]), ['Total surcharges', m(k.surchargesTotal)]], [1], { totalRow: true });
  }

  if (on('tips')) {
    const c = d.close ?? {};
    heading('Tips (for distribution)', 6);
    table(['Type', 'Amount'], [
      ['Card tips', m(c.card_tips)], ...(c.web_tips ? [['Web tips', m(c.web_tips)]] : []), ['Cash tips collected', m(d.tips.cash)],
      ...(cfg.groupSurchargeIsTip ? [['Group surcharge', m(c.group_surcharge)]] : []), ['Total tips', m(k.tipsTotal)],
    ], [1], { totalRow: true });
  }

  if (on('debtors')) {
    const rows = d.debtors.length ? d.debtors : cfg.debtorNames.map((name) => ({ name, amount: null, detail: '' }));
    heading('Accounts (on account)', rows.length + 2);
    table(['Name', 'Amount', 'Description / table'], [...rows.map((x) => [x.name, x.amount ? m(x.amount) : '—', x.detail]), ['Total', m(k.debtorsTotal), '']], [1], { totalRow: true, widths: [55, 30] });
  }

  if (on('discounts')) {
    // Every code the venue uses, with tonight's total; codes used tonight that aren't in the list still show.
    const codes = [...new Set([...cfg.discountCodes, ...d.discounts.map((x) => x.code)])];
    const rows = codes.map((code) => {
      const items = d.discounts.filter((x) => x.code === code && x.amount);
      const amt = items.reduce((a, x) => a + (x.amount ?? 0), 0);
      return [code, amt ? m(amt) : '—', amt ? pctOf(amt) : '—', items.map((x) => x.detail.trim()).filter(Boolean).join('; ')];
    });
    const dd = (k.discountsTotal ?? 0) + (k.deletedAmount ?? 0);
    heading('Discounts, deleted & refunds', rows.length + 4);
    table(['Type', 'Total', '% sales', 'Name – item – reason'], [
      ...rows,
      ['Deleted items', k.deletedAmount ? m(k.deletedAmount) : '—', pctOf(k.deletedAmount), d.deleted.detail],
      ['Refunds', d.refunds.amount ? m(d.refunds.amount) : '—', pctOf(d.refunds.amount), d.refunds.detail],
      ['Total discounts & deleted', m(dd), pctOf(dd), ''],
    ], [1, 2], { totalRow: true, widths: [58, 24, 18] });
  }

  if (on('labour')) {
    const L = d.labour;
    const pctEx = (v: number | null) => (v != null && k.exGst ? `${((v / k.exGst) * 100).toFixed(1)}%` : '—');
    const hasT = tgt.fohPct != null || tgt.bohPct != null || tgt.labourPct != null;
    heading('Labour', 6);
    table(['', 'Hours', 'Wages', '% of ex GST', ...(hasT ? ['Target'] : [])], [
      ['Front of house', L.fohHours != null ? String(L.fohHours) : '—', m(L.fohCost), pctEx(L.fohCost), ...(hasT ? [tgt.fohPct != null ? `${tgt.fohPct}%` : '—'] : [])],
      ['Kitchen', L.bohHours != null ? String(L.bohHours) : '—', m(L.bohCost), pctEx(L.bohCost), ...(hasT ? [tgt.bohPct != null ? `${tgt.bohPct}%` : '—'] : [])],
      ['Total', k.labourHours ? String(k.labourHours) : '—', m(k.labourTotal), k.labourPct != null ? `${k.labourPct}%` : '—', ...(hasT ? [tgt.labourPct != null ? `${tgt.labourPct}%` : '—'] : [])],
    ], hasT ? [1, 2, 3, 4] : [1, 2, 3], { totalRow: true });
    font(7.5); color(DIM); txt(L.source === 'roster' ? 'Hours and wages from the Deputy roster.' : L.source === 'manual' ? 'Hours and wages entered by the manager.' : '', M, y - 2); y += 2;
  }

  if (on('offers')) {
    const offers = d.offers.length ? d.offers : cfg.offers.map((name) => ({ name, uses: null, value: null, note: '' }));
    if (offers.length) {
      heading('Offers', offers.length + 2);
      const uses = offers.reduce((a, o) => a + (o.uses ?? 0), 0), val = offers.reduce((a, o) => a + (o.value ?? 0), 0);
      table(['Offer', 'Uses / covers', '$ value', 'Notes'], [...offers.map((o) => [o.name, o.uses != null ? String(o.uses) : '—', m(o.value), o.note]), ['Total', String(uses), m(val), '']], [1, 2], { totalRow: true, widths: [70, 24, 24] });
    }
  }

  // ---------------- service summary ----------------
  if (on('serviceSummary')) {
    const front = cfg.stationsFront?.length ? cfg.stationsFront : DEFAULT_STATIONS_FRONT;
    const kitchen = cfg.stationsKitchen?.length ? cfg.stationsKitchen : DEFAULT_STATIONS_KITCHEN;
    const topics = cfg.noteTopics?.length ? cfg.noteTopics : DEFAULT_NOTE_TOPICS;
    const names = cfg.services.length ? cfg.services : ['Day'];
    const filled = names.filter((s) => { const x = d.service?.[s]; return x && (Object.values(x.stations).some((v) => v?.trim()) || Object.values(x.notes).some((v) => v?.trim())); });
    if (filled.length) {
      y += 2; heading('Service summary', 10, false);
      for (const s of filled) {
        const x = d.service![s];
        const v = k.services[s];
        ensure(30);
        font(10, true); color(INK); txt(s, M, y + 3);
        font(8); color(DIM);
        txt([v?.covers != null ? `Covers ${v.covers}` : '', v?.perHead != null ? `$/head ${m(v.perHead)}` : '', v?.sales && v.food != null ? `Food ${Math.round((v.food / v.sales) * 100)}% · Bev ${100 - Math.round((v.food / v.sales) * 100)}%` : ''].filter(Boolean).join('  ·  '), M + 30, y + 3);
        y += 6;
        const st = (area: 'front' | 'kitchen', list: string[]) => list.map((n) => [n, x.stations[`${area}:${n}`]?.trim() ?? '']).filter(([, who]) => who);
        const f = st('front', front), kt = st('kitchen', kitchen);
        if (f.length || kt.length) {
          const rowsN = Math.max(f.length, kt.length);
          table(['Front', '', 'Kitchen', ''], Array.from({ length: rowsN }, (_, i) => [f[i]?.[0] ?? '', f[i]?.[1] ?? '', kt[i]?.[0] ?? '', kt[i]?.[1] ?? '']), [], { widths: [24, 67, 24, 67] });
        }
        const notes = topics.map((t) => [t, x.notes[t]?.trim() ?? '']).filter(([, n]) => n);
        if (notes.length) table(['Topic', 'Notes'], notes, [], { widths: [45] });
      }
    }
  }

  if (d.notes.trim()) {
    heading('Manager notes', 6, false);
    font(9); color(INK);
    const lines = doc.splitTextToSize(pdfSafe(d.notes.trim()), CW) as string[];
    for (const l of lines) { ensure(6); doc.text(l, M, y + 3); y += 4.6; }
    y += 4;
  }
  if (p.team.length) {
    const firsts = p.team.map((t) => t.name.split(' ')[0]).join(', ');
    if (p.summary?.team?.length) {
      // Summarised by topic (the full check-ins stay in OpsAlo, under End of day › Team).
      heading(`Team check-ins · ${p.team.length} ${p.team.length === 1 ? 'person' : 'people'} (${firsts})`, 6, false);
      table(['Topic', 'Summary'], p.summary.team.map((t) => [t.topic, t.summary]), [], { widths: [38] });
    } else {
      heading(`Team check-ins · ${p.team.length}`, 6, false);
      table(['Who', 'Area', 'Note'], p.team.flatMap((t) => Object.entries(t.answers).filter(([, v]) => v?.trim()).map(([q, v]) => [t.name, q.replace(/_/g, ' '), v.trim()])), [], { widths: [32, 36] });
    }
  }

  // ---------------- header band + footer on every page ----------------
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFillColor(...GREEN); doc.rect(0, 0, W, 18, 'F');
    doc.setTextColor(255, 255, 255); font(13, true); txt('End of day report', M, 9.5);
    font(8); txt(dayLong, M, 14.2);
    font(10, true); txt('OpsAlo', W - M, 9.5, { align: 'right' }); font(7.5); txt(p.venueName, W - M, 14.2, { align: 'right' });
    font(7.5); color(FAINT);
    txt(`OpsAlo · ${p.venueName} · ${p.date}`, M, 290); txt(`Page ${i} of ${pages}`, W - M, 290, { align: 'right' });
  }
  return doc.output('blob');
}

function shiftIso(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10);
}
